import type { FormField } from "@tellescope/types-models";
import { occupiesOwnPage, subFieldIdsOf } from "../../utils";
import type { FormFieldNode } from "../../utils/types";

/**
 * The enduser's path through the wizard, oldest first. The last node is the field on screen.
 *
 * Every node the walk crossed is here, pass-through ones included, so "what was navigated" and
 * therefore "what is submitted" and "what conditional logic may read" are all the same question.
 * The last node always occupies its own page; pass-through nodes only ever sit below a page.
 */
export type FieldStack = FormFieldNode[];

export const activeNodeOf = (stack: FieldStack): FormFieldNode | undefined => stack[stack.length - 1];

/**
 * The ids whose answers count as navigated: every stacked node plus the visible sub-fields of any
 * stacked Question Group. Sub-fields hidden by an unsatisfied `groupShowCondition` were never shown,
 * so their values are left out.
 */
export const includedFieldIdsOf = (stack: FieldStack, isSubFieldHidden: (fieldId: string) => boolean): Set<string> =>
  stack.reduce<Set<string>>((acc, node) => {
    acc.add(node.value.id);
    subFieldIdsOf(node.value).forEach((subFieldId) => {
      if (!isSubFieldHidden(subFieldId)) acc.add(subFieldId);
    });
    return acc;
  }, new Set());

const isPage =
  (subFieldIds: ReadonlySet<string>, fieldsMap: Record<string, FormField | undefined>) => (node: FormFieldNode) =>
    occupiesOwnPage(node.value, subFieldIds, fieldsMap);

/** Zero-based index of the page on screen: how many page-occupying nodes sit below the active one. */
export const pageIndexOf = (
  stack: FieldStack,
  subFieldIds: ReadonlySet<string>,
  fieldsMap: Record<string, FormField | undefined>
): number => stack.slice(0, -1).filter(isPage(subFieldIds, fieldsMap)).length;

/**
 * The stack after pressing Back: the active node and any pass-through run above the previous page
 * are dropped, so that page becomes active. Unchanged when there is no previous page.
 */
export const popToPreviousPage = (
  stack: FieldStack,
  subFieldIds: ReadonlySet<string>,
  fieldsMap: Record<string, FormField | undefined>
): FieldStack => {
  const previousPageIndex = stack
    .slice(0, -1)
    .reduce<number>((found, node, index) => (isPage(subFieldIds, fieldsMap)(node) ? index : found), -1);
  return previousPageIndex < 0 ? stack : stack.slice(0, previousPageIndex + 1);
};
